// Superadmin → Admin Accounts: create an employee account for a company and
// promote it to admin in one step. Same layout as the other system pages.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ShieldPlus, UserPlus, RefreshCw, Eye, EyeOff, KeyRound, IdCard, Lock, Info, RotateCcw } from "lucide-react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import ReusableDropdown from "../../components/ReusableDropdown";
import AlertModal from "../../components/AlertModal";
import useAdminAccountCreation from "./hooks/useAdminAccountCreation";
import { getAdminAccountStyles, getAdminAccountGlobalCss } from "./styles/adminAccountStyles";
import { text } from "./constants/adminAccountText";

const getScreenSize = () => {
    const width = window.innerWidth;
    return { width, isMobile: width <= 768, isTablet: width > 768 && width <= 1024 };
};

const AdminAccountCreation = () => {
    const { theme } = useContext(ThemeContext);
    const { language: rawLanguage } = useContext(LanguageContext) || { language: "en" };
    const language = rawLanguage === "ar" ? "ar" : "en";
    const t = text[language];

    const [screenSize, setScreenSize] = useState(getScreenSize);
    useEffect(() => {
        const onResize = () => setScreenSize(getScreenSize());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const s = useMemo(() => getAdminAccountStyles(screenSize, theme, language), [screenSize, theme, language]);
    const globalCss = useMemo(() => getAdminAccountGlobalCss(theme), [theme]);
    const [showPassword, setShowPassword] = useState(false);

    const { companies, loadingCompanies, saving, alert, closeAlert, form, onChange, resetForm, handleCreateAdmin } =
        useAdminAccountCreation(t);

    const label = (key, required) => (
        <label style={s.label} htmlFor={`aac-${key}`}>
            {t[key === "sr" ? "name" : key === "phoneNumber" ? "phone" : key]}
            {required ? <span style={s.required}>*</span> : <span style={s.optional}>({t.optional})</span>}
        </label>
    );

    const textInput = (key, { type = "text", placeholder, dir, autoComplete } = {}) => (
        <input
            id={`aac-${key}`}
            type={type}
            dir={dir}
            className="aac-input"
            style={s.input}
            value={form[key]}
            placeholder={placeholder}
            autoComplete={autoComplete}
            onChange={(e) => onChange(key, e.target.value)}
        />
    );

    const iconSize = screenSize.isMobile ? 16 : 20;

    return (
        <div className="aac-scope nx-flush-mobile" style={s.container}>
            <style>{globalCss}</style>

            <div className="nx-page-header" style={s.header}>
                <h1 className="nx-page-title" style={s.title}>
                    <ShieldPlus size={screenSize.isMobile ? 24 : 28} />
                    {t.title}
                </h1>
                <p style={s.subtitle}>{t.subtitle}</p>
            </div>

            <form onSubmit={handleCreateAdmin} noValidate>
                <div style={s.sectionCard}>
                    <div style={s.sectionHeader}>
                        <h3 style={s.sectionTitle}>
                            <KeyRound size={iconSize} />
                            {t.sectionAccount}
                        </h3>
                        <p style={s.sectionSubtitle}>{t.sectionAccountSub}</p>
                    </div>
                    <div style={s.grid}>
                        <div style={s.field}>
                            {label("sr", true)}
                            {textInput("sr", { placeholder: t.namePlaceholder, autoComplete: "off" })}
                        </div>
                        <div style={s.field}>
                            {label("email", true)}
                            {textInput("email", { type: "email", placeholder: t.emailPlaceholder, dir: "ltr", autoComplete: "off" })}
                        </div>
                        <div style={s.field}>
                            {label("company", true)}
                            <ReusableDropdown
                                items={companies}
                                value={form.clientName}
                                onChange={(value) => onChange("clientName", value)}
                                getItemKey={(item) => item.clientName}
                                getItemLabel={(item) => item.clientName}
                                placeholder={loadingCompanies ? t.loadingCompanies : t.companyPlaceholder}
                                searchable={companies.length > 7}
                                disabled={loadingCompanies}
                                theme={theme}
                                language={language}
                            />
                        </div>
                        <div style={s.field}>
                            {label("password", true)}
                            <div style={s.passwordWrap}>
                                <input
                                    id="aac-password"
                                    type={showPassword ? "text" : "password"}
                                    dir="ltr"
                                    className="aac-input"
                                    style={{ ...s.input, paddingInlineEnd: "48px", textAlign: language === "ar" ? "right" : "left" }}
                                    value={form.password}
                                    placeholder={t.passwordPlaceholder}
                                    autoComplete="new-password"
                                    onChange={(e) => onChange("password", e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="aac-eye"
                                    style={s.eyeBtn}
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? t.hidePassword : t.showPassword}
                                    title={showPassword ? t.hidePassword : t.showPassword}
                                >
                                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={s.sectionCard}>
                    <div style={s.sectionHeader}>
                        <h3 style={s.sectionTitle}>
                            <IdCard size={iconSize} />
                            {t.sectionExtra}
                        </h3>
                        <p style={s.sectionSubtitle}>{t.sectionExtraSub}</p>
                    </div>
                    <div style={s.grid}>
                        <div style={s.field}>
                            {label("infinityCode", false)}
                            {textInput("infinityCode", { placeholder: t.infinityCodePlaceholder, dir: "ltr" })}
                        </div>
                        <div style={s.field}>
                            {label("clientCode", false)}
                            {textInput("clientCode", { placeholder: t.clientCodePlaceholder, dir: "ltr" })}
                        </div>
                        <div style={s.field}>
                            {label("phoneNumber", false)}
                            {textInput("phoneNumber", { type: "tel", placeholder: t.phonePlaceholder, dir: "ltr" })}
                        </div>
                        <div style={s.field}>
                            {label("location", false)}
                            {textInput("location", { placeholder: t.locationPlaceholder })}
                        </div>
                    </div>
                </div>

                <div style={s.sectionCard}>
                    <div style={s.notices}>
                        <div style={s.notice}>
                            <Info size={16} style={{ flexShrink: 0, marginTop: 3 }} />
                            <span>{t.roleNotice}</span>
                        </div>
                        <div style={s.notice}>
                            <Lock size={16} style={{ flexShrink: 0, marginTop: 3 }} />
                            <span>{t.passwordNotice}</span>
                        </div>
                    </div>
                    <div style={s.actions}>
                        <button type="button" style={s.btnSecondary(saving)} disabled={saving} onClick={resetForm}>
                            <RotateCcw size={16} />
                            {t.reset}
                        </button>
                        <button type="submit" className="aac-btn-primary" style={s.btnPrimary(saving || loadingCompanies)} disabled={saving || loadingCompanies}>
                            {saving ? <RefreshCw size={16} style={{ animation: "aacSpin 1s linear infinite" }} /> : <UserPlus size={16} />}
                            {saving ? t.creating : t.create}
                        </button>
                    </div>
                </div>
            </form>

            <AlertModal
                open={alert.open}
                title={alert.title}
                message={alert.message}
                variant={alert.variant}
                onConfirm={closeAlert}
                onClose={closeAlert}
                theme={theme}
                language={language}
            />
        </div>
    );
};

export default AdminAccountCreation;
